import { useState } from "react";
import { Link } from "react-router";

import {
  useActivateProjectBackgroundMutation,
  type AdoptionPin,
  type LibraryAsset,
} from "@/api/library";
import { useCapability } from "@/auth/SessionContext";
import { POLICY_UNREAD_SENTENCE, pinRequest } from "@/lib/adoptions";
import { libraryAssetHref } from "@/lib/librarySections";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { Notice } from "@/ui/Notice";
import { Tooltip } from "@/ui/Tooltip";
import { AssetStateBadges } from "@/ui/library/AssetStateBadges";
import { PreflightReasons } from "@/ui/adoptions/PreflightReasons";

/**
 * Which shared interview profile this project runs, and the areas it asks in
 * order (#914).
 *
 * ## WHY THIS IS A SEPARATE CONTROL ABOVE THE ROWS
 *
 * The Lovable Project BG screen is compact adopted-domain rows under one
 * `+ Adopt from library`. The S7 contract's explicit correction for that screen
 * is "exact versions, real CAS, separate profile selection; no duplicate domain
 * list" — so the profile does NOT become a seventh row beside the six domains
 * (its pin is filtered out of them), and the domain list is not repeated here.
 * `ProjectSectionPane` declared this slot in #909 for exactly this leaf.
 *
 * A profile is a genuinely different kind of choice from a domain: one project
 * holds at most one, it decides HOW every area is asked rather than WHICH areas
 * are asked, and its coordinate is what the runtime reads as
 * `flow_config.bg_variant`.
 *
 * ## WHY THERE IS NO "SELECT A PROFILE" BUTTON WHEN NONE IS PINNED
 *
 * Because there is no write it could perform. The Editor Service refuses a
 * profile with no domain at its variant (`lonely_profile`) and domains with no
 * profile behind them (`unbacked_domains`) with the same
 * `placement.background_profile_conflict`, so the FIRST coherent background set
 * a project can hold necessarily carries both — verified against the real
 * service, in both orders, before this surface was written. A button here would
 * be the dead control ADR 0001 forbids: every press would refuse.
 *
 * So the unpinned state STATES what the project runs today and names the one
 * control that can change it — `Adopt from library`, which offers the profiles
 * beside the domains for that reason.
 *
 * ## THE TWO WRITES THIS CONTROL OWNS, AND WHY THEY ARE THESE TWO
 *
 * **Moving to another version of the same profile** is the one profile change
 * that leaves the domains coherent: the coordinate is the variant and it is
 * immutable, so a new VERSION serves the same interview. The body is every
 * domain pin the project holds at the exact version it holds, plus the profile
 * pin that moved — "profile replacement never replaces domain pins" is the
 * request this control builds, not a promise about the server.
 *
 * **Resetting the background** sends the EMPTY set, and that is the correction
 * the #914 review forced. This control previously sent the domains without the
 * profile, which reads like "remove the profile" and is refused every time
 * (`unbacked_domains`) — and dropping the domains one at a time dead-ends too,
 * because the last removal leaves a `lonely_profile`. Verified against the real
 * service: from a profile plus its domains the ONLY accepted exit is the empty
 * set. So the control performs that exit, names what it removes before it
 * happens, and does not pretend a profile can be dropped on its own.
 *
 * A profile SWAP is deliberately not here: another profile is necessarily
 * another variant, so its areas have to move with it, and only a surface that
 * states the whole resulting set can express that. That surface is the
 * section's `Adopt from library` selection.
 */

/** What a project with no profile pin actually runs, stated rather than blank. */
const INHERITED_SENTENCE =
  "This project runs the base clinical pack's own interview behaviour — its opening, tone and the way it moves between background areas. No interview profile is adopted.";

const INHERITED_HOW =
  "An interview profile is adopted together with the background areas that belong to it, through Adopt from library above: the clinical engine cannot run a profile whose areas are not adopted, or areas whose profile is not.";

export function BackgroundProfileControl({
  projectId,
  adoptionsVersion,
  profilePin,
  profileAsset,
  domainPins,
  sequence,
  readsCurrent,
  adoptionsCurrent,
  policyUnread,
}: {
  projectId: string;
  /** The pin-set token this surface LOADED, sent back as the write's guard. */
  adoptionsVersion: string;
  /** The project's single background-profile pin, or `null`. */
  profilePin: AdoptionPin | null;
  /** The catalog row for that pin, when the catalog named it. */
  profileAsset: LibraryAsset | null;
  /** Every background-domain pin the project holds, exactly as held. */
  domainPins: readonly AdoptionPin[];
  /**
   * The background areas in the order the runtime will ask them, read off the
   * EXACT PINNED versions — or `null` for a pin whose position is not in hand.
   */
  sequence: readonly { id: string; name: string; order: number | null }[];
  /** Whether the catalog and the pin set are both current successful reads. */
  readsCurrent: boolean;
  /**
   * Whether the PIN SET alone is a current successful read.
   *
   * Separate from `readsCurrent` because the reset needs only the token: its
   * body is the empty set, so a catalog outage cannot make it wrong — and
   * gating it on the catalog removed the sole recovery path exactly in the
   * degraded state that needs it (#914 review, and #612's removal invariant).
   */
  adoptionsCurrent: boolean;
  /** Whether the served placement policy is unread (#634). */
  policyUnread: boolean;
}) {
  const write = useCapability("draft.write");
  const activate = useActivateProjectBackgroundMutation(projectId);
  const [resetting, setResetting] = useState(false);

  // Names for a refusal, from the pins this surface can name. A refusal can
  // arrive against the profile or against any domain carried with it, so both
  // halves are here — the same rule the picker's own map follows.
  const names = new Map<string, string>();
  if (profileAsset) {
    names.set(profileAsset.id, profileAsset.name);
  }
  for (const item of sequence) {
    names.set(item.id, item.name);
  }

  /** Whether the write landed — `false` for a refusal, which is rendered below. */
  async function activateProfile(
    items: readonly AdoptionPin[],
    /** Which read this write actually depends on. */
    ready = readsCurrent,
  ): Promise<boolean> {
    if (!ready) {
      return false;
    }
    try {
      await activate.mutateAsync({
        // The token this surface LOADED, never one re-read at submit time —
        // the discipline every adoption write in this shell keeps.
        expectedAdoptionsVersion: adoptionsVersion,
        items: items.map(pinRequest),
      });
      return true;
    } catch {
      // Rendered below, beside the state that motivated the attempt.
      return false;
    }
  }

  const refusal =
    write.reason ??
    (!readsCurrent
      ? "This project's adoptions and the Clinical library must load before the interview profile can be changed."
      : policyUnread
        ? POLICY_UNREAD_SENTENCE
        : null);

  return (
    <section className="space-y-3 rounded-md border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h3 className="text-sm font-medium text-foreground">
            Interview profile
          </h3>
          {profilePin === null ? (
            <p className="text-sm text-muted-foreground">
              {INHERITED_SENTENCE}
            </p>
          ) : (
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-foreground">
              {profileAsset ? (
                <Link
                  className="font-medium underline underline-offset-2"
                  to={libraryAssetHref(profileAsset.assetType, profileAsset.id)}
                >
                  {profileAsset.name}
                </Link>
              ) : (
                // The catalog did not name it. The pin is still real and still
                // the project's, so it is shown by the identity the pin set
                // holds rather than dropped from the surface.
                <span className="font-mono text-xs">{profilePin.assetId}</span>
              )}
              <span className="font-mono text-xs text-muted-foreground">
                {profilePin.version}
              </span>
              {profileAsset ? (
                <AssetStateBadges capabilities={profileAsset.capabilities} />
              ) : null}
            </p>
          )}
        </div>

        {profilePin === null ? null : (
          <div className="flex flex-wrap items-center gap-2">
            {/* MOVING TO ANOTHER VERSION IS THE REPLACEMENT THAT KEEPS THE
                DOMAINS. The profile's coordinate is its variant and is
                immutable, so a different VERSION of the same profile leaves
                every domain pin coherent — which is what makes this the one
                profile change a project can make without touching its areas.
                A different profile is a different variant, and the areas have
                to move with it; that is the picker's write, not this one's. */}
            {profileAsset !== null &&
            profileAsset.latestVersion !== profilePin.version ? (
              <Tooltip
                content={
                  refusal ??
                  `Move this project to ${profileAsset.latestVersion} of the same interview profile. Every adopted background area keeps the exact version it holds.`
                }
              >
                <Button
                  variant="outline"
                  size="sm"
                  disabled={refusal !== null || activate.isPending}
                  onClick={() =>
                    void activateProfile([
                      {
                        ...profilePin,
                        version: profileAsset.latestVersion,
                      },
                      ...domainPins,
                    ])
                  }
                >
                  Update to {profileAsset.latestVersion}
                </Button>
              </Tooltip>
            ) : null}

            <Tooltip
              content={
                // NEVER the policy read on a removal (#612): un-pinning is the
                // recovery path out of every degraded state and no failed read
                // may take it away. The capability and the pin set still gate
                // it, because neither is optional to build the request.
                write.reason ??
                (!adoptionsCurrent
                  ? "This project's adoptions must load before the background can be reset."
                  : "Return this project's background to the base clinical pack's, removing this interview profile and the areas adopted with it.")
              }
            >
              <Button
                variant="outline"
                size="sm"
                disabled={
                  // NOT `readsCurrent`: the request is the empty set and needs
                  // only the token this surface loaded, so a catalog outage may
                  // not take the recovery away (#612).
                  write.reason !== null ||
                  !adoptionsCurrent ||
                  activate.isPending
                }
                onClick={() => {
                  // A FRESH ATTEMPT REPORTS ITSELF. One mutation serves both
                  // writes, so an earlier failed update — or an earlier failed
                  // reset — would otherwise appear inside a newly opened
                  // confirmation as though this one had already failed.
                  activate.reset();
                  setResetting(true);
                }}
              >
                Reset to base pack
              </Button>
            </Tooltip>
          </div>
        )}
      </div>

      {profilePin === null ? (
        <p className="text-xs text-muted-foreground">{INHERITED_HOW}</p>
      ) : null}

      {/* THE ACTIVE AREA SEQUENCE, which is a fact about the profile rather
          than about any one area: this is the interview it will run, in the
          order the pinned documents declare. Compact and read-only — the rows
          below are where an area is opened or changed. */}
      {sequence.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            {/* NOT "in interview order" when a position is missing. A row whose
                pinned version has not been read keeps its pin-set place, so the
                line is only partly ordered — and a label that claimed otherwise
                would be the surface asserting something the reads did not
                support. The per-area note below says which one. */}
            {sequence.some((item) => item.order === null)
              ? "Background areas adopted:"
              : "Background areas, in interview order:"}{" "}
          </span>
          {sequence.map((item, index) => (
            <span key={item.id}>
              {index > 0 ? " · " : ""}
              {item.name}
              {item.order === null ? " (position not read)" : ""}
            </span>
          ))}
        </p>
      ) : null}

      {resetting ? (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next && !activate.isPending) {
              setResetting(false);
            }
          }}
        >
          <DialogContent
            busy={activate.isPending}
            title="Reset background to the base pack"
            description="This project's background returns to the base clinical pack's own interview — its opening, tone, transitions and areas."
            footer={
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="outline"
                  disabled={activate.isPending}
                  onClick={() => setResetting(false)}
                >
                  Cancel
                </Button>
                <Button
                  // FAIL CLOSED IF THE PIN SET STOPS BEING READ WHILE THIS IS
                  // OPEN. `adoptionsVersion` is the token this surface loaded,
                  // and the caller substitutes `"0"` — a REAL token, meaning
                  // "this project has never written pins" — once the read is no
                  // longer current. Submitting that for a project that holds
                  // pins is a request built from a fallback, and the guard
                  // inside `activateProfile` would only make the press do
                  // nothing at all, silently. So the control is disabled and
                  // the reason is stated above it.
                  disabled={activate.isPending || !adoptionsCurrent}
                  onClick={() => {
                    // THE EMPTY SET, which is the only exit the service accepts
                    // from a profile plus its areas. Sending the areas without
                    // the profile is refused, and so is the profile without
                    // them — so this removes both, and says so above before it
                    // is pressed.
                    //
                    // CLOSED ONLY ON SUCCESS. A refusal renders under the
                    // control, and closing the confirmation over it would hide
                    // the one sentence that says why nothing happened.
                    void activateProfile([], adoptionsCurrent).then((ok) => {
                      if (ok) {
                        setResetting(false);
                      }
                    });
                  }}
                >
                  Reset background
                </Button>
              </div>
            }
          >
            <div className="space-y-2 text-sm text-foreground">
              {adoptionsCurrent ? null : (
                // The read went away UNDER an open confirmation. Nothing has
                // been written, the selection is unchanged, and the reset
                // becomes available again on its own when the pin set reloads.
                <Notice role="alert">
                  This project&apos;s adoptions are no longer loaded, so the
                  reset cannot be applied against the pin set this confirmation
                  was opened on. Nothing has been changed; it becomes available
                  again when they reload.
                </Notice>
              )}
              {activate.isError ? (
                <Notice role="alert" tone="destructive">
                  <p>
                    {activate.error instanceof Error
                      ? activate.error.message
                      : "The Editor Service refused this reset. Nothing was written."}
                  </p>
                  <PreflightReasons error={activate.error} names={names} />
                </Notice>
              ) : null}
              {/* WHAT THIS REMOVES IS A FACT ABOUT THE PIN SET, so it is shown
                  only while the pin set is READ. Every value in it comes from
                  the caller's view of the adoptions, which empties when that
                  read stops being current — leaving "the interview profile at"
                  with no name and "0 adopted background areas" standing as a
                  description of what would be removed. The notice above already
                  says what the state is; a list nobody can stand behind adds
                  nothing to it. */}
              {adoptionsCurrent ? (
                <>
                  <p>This removes:</p>
                  <ul className="list-disc space-y-1 pl-5">
                    <li>
                      the interview profile{" "}
                      <span className="font-medium">
                        {profileAsset?.name ?? profilePin?.assetId}
                      </span>{" "}
                      at {profilePin?.version};
                    </li>
                    <li>
                      {domainPins.length}{" "}
                      {domainPins.length === 1
                        ? "adopted background area"
                        : "adopted background areas"}
                      .
                    </li>
                  </ul>
                </>
              ) : null}
              {/* WHY BOTH GO, rather than leaving it to be discovered from a
                  refusal: the clinical engine cannot run areas whose profile is
                  not adopted, so there is no state in which one survives the
                  other. The library assets themselves are untouched and can be
                  adopted again.

                  Beside the list, because it explains that list — with the pin
                  set unread there is no list, and "the areas go with the
                  profile" would be describing a removal the surface has just
                  said it cannot describe. */}
              {adoptionsCurrent ? (
                <p className="text-muted-foreground">
                  The areas go with the profile because the clinical engine
                  cannot run them without one. Nothing is removed from the
                  shared library, and either can be adopted again.
                </p>
              ) : null}
            </div>
          </DialogContent>
        </Dialog>
      ) : null}

      {activate.isError ? (
        <Notice role="alert" tone="destructive">
          <p>
            {activate.error instanceof Error
              ? activate.error.message
              : "The Editor Service refused this change. Nothing was written."}
          </p>
          {/* THE CLINICAL EXPLANATION, from the served vocabulary rather than
              from a storage path. `placement.background_profile_conflict` reads
              as "the background profile and the adopted domains do not describe
              one complete interview" — which is what a clinician has to act on,
              and it names no directory, file or coordinate. */}
          <PreflightReasons error={activate.error} names={names} />
        </Notice>
      ) : null}
    </section>
  );
}
